import { audiencerateApi } from '@/services/audiencerate';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { buildMockPerformanceAudienceDashboard } from './mockDashboard';
import {
  CampaignPerformanceAudiencesResponse,
  CampaignPerformanceDashboardResponse,
  CampaignPerformanceSourcesResponse,
  GetCampaignPerformanceAudiencesArgs,
  GetCampaignPerformanceDashboardArgs,
  GetCampaignPerformanceSourcesArgs,
  PerformanceAudienceDashboardArgs,
  PerformanceAudienceDashboardResponse,
} from './types';

export const USE_MOCK_PERFORMANCE_AUDIENCE_API = false;

const MOCK_DELAY_MS = 100;

const mockDelay = () => new Promise<void>((resolve) => setTimeout(resolve, MOCK_DELAY_MS));

type PerformanceAudienceQueryResult<T> = { data: T; error?: undefined; meta?: unknown } | { error: FetchBaseQueryError; data?: undefined; meta?: unknown };

export const performanceAudienceApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    getPerformanceAudienceDashboard: build.query<PerformanceAudienceDashboardResponse, PerformanceAudienceDashboardArgs>({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        if (USE_MOCK_PERFORMANCE_AUDIENCE_API) {
          await mockDelay();
          return { data: buildMockPerformanceAudienceDashboard(args) };
        }

        const { workspaceId, ...params } = args;
        return (await baseQuery({
          url: `/v1/workspaces/${workspaceId}/performance-audience`,
          params,
        })) as PerformanceAudienceQueryResult<PerformanceAudienceDashboardResponse>;
      },
      providesTags: (_result, _error, { workspaceId }) => [{ type: 'PerformanceAudienceDashboard', id: workspaceId }],
    }),
    getCampaignPerformanceAudiences: build.query<CampaignPerformanceAudiencesResponse, GetCampaignPerformanceAudiencesArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaign-performance/audiences`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { workspaceId }) => [{ type: 'CampaignPerformanceAudiences', id: workspaceId }],
    }),
    getCampaignPerformanceSources: build.query<CampaignPerformanceSourcesResponse, GetCampaignPerformanceSourcesArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaign-performance/sources`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { workspaceId }) => [{ type: 'CampaignPerformanceSources', id: workspaceId }],
    }),
    getCampaignPerformanceDashboard: build.query<CampaignPerformanceDashboardResponse, GetCampaignPerformanceDashboardArgs>({
      query: ({ workspaceId, ...params }) => ({
        url: `/v1/workspaces/${workspaceId}/campaign-performance/dashboard`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { workspaceId }) => [{ type: 'CampaignPerformanceDashboard', id: workspaceId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPerformanceAudienceDashboardQuery,
  useGetCampaignPerformanceAudiencesQuery,
  useGetCampaignPerformanceSourcesQuery,
  useGetCampaignPerformanceDashboardQuery,
} = performanceAudienceApi;
